import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';

export default function MeterPhotoModal({
  isOpen,
  onClose,
  meters = [],
  selectedMeter = '',
  deviceDetails = {},
  baseUrl = '',
  Token = '',
  onSave,
}) {
  const [meterId, setMeterId] = useState(selectedMeter);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [serverImageSrc, setServerImageSrc] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [fileError, setFileError] = useState('');
  const [isDetectingGps, setIsDetectingGps] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const activeMid = selectedMeter && selectedMeter !== 'All' ? selectedMeter : meters[0]?.value || '';
      setMeterId(activeMid);

      const matchedMeter = meters.find((m) => (m.value || m.productId) === activeMid) || {};
      const activeDev = activeMid === selectedMeter ? deviceDetails : matchedMeter;

      const latVal =
        activeDev.latitude !== undefined && activeDev.latitude !== null && !isNaN(Number(activeDev.latitude)) && Number(activeDev.latitude) !== 0
          ? String(activeDev.latitude)
          : activeDev.lat !== undefined && activeDev.lat !== null && !isNaN(Number(activeDev.lat))
          ? String(activeDev.lat)
          : '28.541505';

      const lngVal =
        activeDev.longitude !== undefined && activeDev.longitude !== null && !isNaN(Number(activeDev.longitude)) && Number(activeDev.longitude) !== 0
          ? String(activeDev.longitude)
          : activeDev.lng !== undefined && activeDev.lng !== null && !isNaN(Number(activeDev.lng))
          ? String(activeDev.lng)
          : '77.332897';

      setLatitude(latVal);
      setLongitude(lngVal);
      setSelectedFile(null);
      setPreviewUrl(null);
      setImageLoadError(false);
      setFileError('');

      const photoData = activeDev.meterPhoto;
      if (photoData?.fileName || photoData?.storedFileName) {
        setServerImageSrc(`${baseUrl}/device/meter-photo/${activeMid}?t=${Date.now()}`);
      } else {
        setServerImageSrc(null);
      }
    }
  }, [isOpen, selectedMeter, deviceDetails, meters, baseUrl]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFileError('Please select a valid image file (JPG, PNG, WebP).');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setFileError('Image file size must be less than 10MB.');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    setFileError('');
    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleDetectLocation = () => {
    if (typeof window !== 'undefined' && 'geolocation' in navigator) {
      setIsDetectingGps(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setIsDetectingGps(false);
          if (pos?.coords) {
            setLatitude(String(pos.coords.latitude.toFixed(6)));
            setLongitude(String(pos.coords.longitude.toFixed(6)));
          }
        },
        (err) => {
          setIsDetectingGps(false);
          alert('Could not retrieve current location: ' + err.message);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const latNum = parseFloat(latitude);
    const lngNum = parseFloat(longitude);

    if (isNaN(latNum) || latNum < -90 || latNum > 90) {
      alert('Please enter a valid Latitude between -90 and 90.');
      return;
    }

    if (isNaN(lngNum) || lngNum < -180 || lngNum > 180) {
      alert('Please enter a valid Longitude between -180 and 180.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (onSave) {
        await onSave({
          meterId: meterId || selectedMeter,
          latitude: latNum,
          longitude: lngNum,
          photoFile: selectedFile,
        });
      }
      onClose();
    } catch (err) {
      console.error('Error saving meter photo:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const matchedMeterObj = meters.find((m) => (m.value || m.productId) === meterId) || {};
  const meterDisplayName = matchedMeterObj.label || `${meterId} · ${matchedMeterObj.name || 'Borewell'}`;
  const isSingleMeterActive = Boolean(selectedMeter && selectedMeter !== 'All');

  const existingPhotoName =
    (meterId === selectedMeter ? deviceDetails.meterPhoto?.fileName : matchedMeterObj.meterPhoto?.fileName) ||
    (meterId === selectedMeter ? deviceDetails.meterPhoto?.storedFileName : matchedMeterObj.meterPhoto?.storedFileName);

  const handleMeterSelectChange = (newMid) => {
    setMeterId(newMid);
    const matched = meters.find((m) => (m.value || m.productId) === newMid) || {};
    if (matched) {
      if (matched.latitude !== undefined && matched.latitude !== null && !isNaN(Number(matched.latitude)) && Number(matched.latitude) !== 0) {
        setLatitude(String(matched.latitude));
      } else {
        setLatitude('28.541505');
      }
      if (matched.longitude !== undefined && matched.longitude !== null && !isNaN(Number(matched.longitude)) && Number(matched.longitude) !== 0) {
        setLongitude(String(matched.longitude));
      } else {
        setLongitude('77.332897');
      }
      setSelectedFile(null);
      setPreviewUrl(null);
      setImageLoadError(false);
      if (matched.meterPhoto?.fileName || matched.meterPhoto?.storedFileName) {
        setServerImageSrc(`${baseUrl}/device/meter-photo/${newMid}?t=${Date.now()}`);
      } else {
        setServerImageSrc(null);
      }
    }
  };

  const showPhotoPreview = Boolean(previewUrl || (serverImageSrc && !imageLoadError));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(11, 46, 69, 0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'fade .2s ease',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 24px 50px -12px rgba(11, 46, 69, 0.35)',
          width: '100%',
          maxWidth: '560px',
          padding: '24px 28px 26px',
          position: 'relative',
          maxHeight: '92vh',
          overflowY: 'auto',
          animation: 'pop .2s ease',
        }}
      >
        {/* Close Icon */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            border: 0,
            background: 'none',
            cursor: 'pointer',
            padding: '6px',
            color: '#0b2e45',
            display: 'grid',
            placeItems: 'center',
            borderRadius: '6px',
            transition: 'background .15s',
          }}
          title="Close modal"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Modal Header */}
        <div style={{ marginBottom: '16px' }}>
          <h2
            style={{
              margin: '0 0 4px',
              fontSize: '20px',
              fontWeight: 800,
              color: '#0d3b5e',
              letterSpacing: '-.01em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#0284c7" strokeWidth="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Meter Photo & GPS Location
          </h2>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
            Upload on-site meter photo and configure exact GPS latitude and longitude
          </p>
        </div>

        {/* Target Meter Display */}
        <div
          style={{
            marginBottom: '16px',
            padding: '9px 14px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: '12.5px', color: '#4b5563', fontWeight: 600 }}>Target Meter:</span>
          {isSingleMeterActive ? (
            <span style={{ fontSize: '13.5px', fontWeight: 750, color: '#0f766e', background: '#ecfdf5', padding: '3px 10px', borderRadius: '6px' }}>
              {meterDisplayName}
            </span>
          ) : (
            <select
              value={meterId}
              onChange={(e) => handleMeterSelectChange(e.target.value)}
              style={{
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                padding: '4px 10px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#0d3b5e',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              {meters.filter((m) => m.value && m.value !== 'All').map((m) => (
                <option key={m.value || m.productId} value={m.value || m.productId}>
                  {m.label || m.value}
                </option>
              ))}
            </select>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Photo Preview & Dropzone */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
              Meter Photo (JPG, PNG, WebP)
            </label>

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
            />

            {/* If there is a preview or existing photo */}
            {showPhotoPreview ? (
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '12px',
                  background: '#f8fafc',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <img
                  src={previewUrl || serverImageSrc}
                  alt="Meter Preview"
                  onError={() => {
                    if (!previewUrl) {
                      setImageLoadError(true);
                    }
                  }}
                  style={{
                    width: '100%',
                    maxHeight: '220px',
                    objectFit: 'contain',
                    background: '#0b2e45',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '10px' }}>
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '340px' }}>
                    {previewUrl ? `Selected: ${selectedFile?.name}` : `Current: ${existingPhotoName || 'Meter Photo'}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      padding: '5px 12px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      color: '#0284c7',
                      background: '#e0f2fe',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    Change Photo
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed #93c5fd',
                  borderRadius: '12px',
                  padding: '22px 16px',
                  textAlign: 'center',
                  background: '#f8fafc',
                  cursor: 'pointer',
                  transition: 'all .2s ease',
                }}
              >
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" style={{ margin: '0 auto 6px' }}>
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <p style={{ margin: '0 0 2px', fontSize: '13.5px', fontWeight: 600, color: '#0d3b5e' }}>
                  Click to select or drag & drop meter photo
                </p>
                <span style={{ fontSize: '11.5px', color: '#94a3b8' }}>Supports JPG, PNG, WebP up to 10MB</span>
              </div>
            )}

            {fileError && (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>
                {fileError}
              </p>
            )}
          </div>

          {/* GPS Coordinates Section */}
          <div style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontSize: '12.5px', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                GPS Coordinates
              </label>
              <button
                type="button"
                onClick={handleDetectLocation}
                disabled={isDetectingGps}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#0369a1',
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '6px',
                  cursor: isDetectingGps ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M2 12h20" />
                  <circle cx="12" cy="12" r="7" />
                </svg>
                {isDetectingGps ? 'Detecting...' : 'Fetch My GPS'}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: '#64748b', marginBottom: '3px' }}>
                  Latitude
                </span>
                <input
                  type="text"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  placeholder="e.g. 28.541505"
                  required
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13.5px',
                    color: '#0d3b5e',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: '#64748b', marginBottom: '3px' }}>
                  Longitude
                </span>
                <input
                  type="text"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  placeholder="e.g. 77.332897"
                  required
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13.5px',
                    color: '#0d3b5e',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              borderTop: '1px solid #f1f5f9',
              paddingTop: '16px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '9px 18px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontSize: '13.5px',
                fontWeight: 600,
                color: '#475569',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '9px 20px',
                borderRadius: '8px',
                border: 'none',
                background: '#0284c7',
                fontSize: '13.5px',
                fontWeight: 700,
                color: '#fff',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(2, 132, 199, 0.35)',
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save & Update Meter Photo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
